import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';

/**
 * Breakpoint definitions matching Tailwind CSS defaults
 */
export const BREAKPOINTS = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
} as const;

export type BreakpointKey = keyof typeof BREAKPOINTS;

/**
 * Device type classification based on screen width
 */
export type DeviceType = 'mobile' | 'tablet' | 'desktop';

/**
 * Responsive context value interface
 */
export interface ResponsiveContextValue {
  // Core state
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  deviceType: DeviceType;

  // Breakpoint helpers
  isAbove: (breakpoint: BreakpointKey) => boolean;
  isBelow: (breakpoint: BreakpointKey) => boolean;
  isBetween: (min: BreakpointKey, max: BreakpointKey) => boolean;

  // Screen dimensions
  screenWidth: number;
  screenHeight: number;

  // Orientation
  isLandscape: boolean;
  isPortrait: boolean;

  // Behavior flags - these drive different UX patterns
  behavior: {
    /**
     * Whether clicking an item should navigate to a full-screen detail view
     * instead of showing a side panel overlay
     * true on mobile, false on desktop
     */
    useFullScreenDetail: boolean;

    /**
     * Whether the bottom navigation bar is visible
     * true on mobile, false on desktop (sidebar instead)
     */
    showBottomNav: boolean;

    /**
     * Whether to show the desktop sidebar
     * false on mobile, true on desktop
     */
    showSidebar: boolean;

    /**
     * Whether to use compact card layouts
     * true on mobile, false on desktop
     */
    useCompactCards: boolean;

    /**
     * Whether to enable touch gestures (swipe to go back, etc.)
     * true on mobile/tablet, false on desktop
     */
    enableTouchGestures: boolean;

    /**
     * Whether modals should be full-screen
     * true on mobile, false on desktop
     */
    useFullScreenModals: boolean;
  };

  // Styling helpers - pre-computed class strings for common patterns
  styles: {
    /** Container padding: p-4 on mobile, p-6/p-8 on desktop */
    containerPadding: string;

    /** Card padding: p-3 on mobile, p-4 on desktop */
    cardPadding: string;

    /** Gap between items: gap-2 on mobile, gap-4 on desktop */
    itemGap: string;

    /** Header height class */
    headerHeight: string;

    /** Text size for body: text-sm on mobile, text-base on desktop */
    bodyTextSize: string;

    /** Text size for headings */
    headingTextSize: string;
  };
}

export const ResponsiveContext = createContext<ResponsiveContextValue | undefined>(undefined);

interface ResponsiveProviderProps {
  children: ReactNode;
}

/**
 * Touch-first device: no hover and a coarse pointer. A phone or tablet, never
 * a desktop with a mouse — a touchscreen laptop keeps hover and a fine
 * primary pointer, so it stays false. Evaluated once; a device does not
 * change its input class mid-session.
 */
const IS_TOUCH_DEVICE =
  typeof window !== 'undefined' &&
  window.matchMedia('(hover: none) and (pointer: coarse)').matches;

/**
 * Determines device type based on screen dimensions.
 *
 * A phone rotated to landscape is still a phone: on touch devices the mobile
 * cut uses the SHORTER viewport side, so crossing 768 of width by rotating
 * never flips the layout to desktop and back — which remounted every mounted
 * detail screen at each crossing. Non-touch windows (a real desktop being
 * resized) keep the width-based behaviour unchanged, as does the
 * tablet/desktop distinction.
 */
function getDeviceType(width: number, height: number): DeviceType {
  const mobileSide = IS_TOUCH_DEVICE ? Math.min(width, height) : width;
  if (mobileSide < BREAKPOINTS.md) return 'mobile';
  if (width < BREAKPOINTS.lg) return 'tablet';
  return 'desktop';
}

// Debug flag - set to true to see responsive context logs
const DEBUG_RESPONSIVE = false;

export function ResponsiveProvider({ children }: ResponsiveProviderProps) {
  // Get initial dimensions synchronously
  const getInitialWidth = () => {
    if (typeof window === 'undefined') return BREAKPOINTS.lg;
    const width = window.innerWidth;
    if (DEBUG_RESPONSIVE) {
      console.log('[ResponsiveContext] Initial width:', width, 'isMobile:', width < BREAKPOINTS.md);
    }
    return width;
  };

  const getInitialHeight = () => {
    if (typeof window === 'undefined') return 800;
    return window.innerHeight;
  };

  const [screenWidth, setScreenWidth] = useState(getInitialWidth);
  const [screenHeight, setScreenHeight] = useState(getInitialHeight);

  // Update dimensions on resize with debounce
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;

    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        const newWidth = window.innerWidth;
        const newHeight = window.innerHeight;
        if (DEBUG_RESPONSIVE) {
          console.log('[ResponsiveContext] Resize:', newWidth, 'x', newHeight, 'isMobile:', newWidth < BREAKPOINTS.md);
        }
        setScreenWidth(newWidth);
        setScreenHeight(newHeight);
      }, 100); // Debounce for performance
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeoutId);
    };
  }, []);

  // Core computed values
  const deviceType = useMemo(() => {
    const type = getDeviceType(screenWidth, screenHeight);
    if (DEBUG_RESPONSIVE) {
      console.log('[ResponsiveContext] Computing deviceType:', type, 'from screenWidth:', screenWidth, 'breakpoint md:', BREAKPOINTS.md);
    }
    return type;
  }, [screenWidth, screenHeight]);
  const isMobile = deviceType === 'mobile';
  const isTablet = deviceType === 'tablet';
  const isDesktop = deviceType === 'desktop';

  // Breakpoint helpers
  const isAbove = useCallback((breakpoint: BreakpointKey) => {
    return screenWidth >= BREAKPOINTS[breakpoint];
  }, [screenWidth]);

  const isBelow = useCallback((breakpoint: BreakpointKey) => {
    return screenWidth < BREAKPOINTS[breakpoint];
  }, [screenWidth]);

  const isBetween = useCallback((min: BreakpointKey, max: BreakpointKey) => {
    return screenWidth >= BREAKPOINTS[min] && screenWidth < BREAKPOINTS[max];
  }, [screenWidth]);

  // Orientation
  const isLandscape = screenWidth > screenHeight;
  const isPortrait = !isLandscape;

  // Behavior flags - computed based on device type
  const behavior = useMemo(() => ({
    useFullScreenDetail: isMobile,
    showBottomNav: isMobile,
    showSidebar: !isMobile,
    useCompactCards: isMobile,
    enableTouchGestures: isMobile || isTablet,
    useFullScreenModals: isMobile,
  }), [isMobile, isTablet]);

  // Pre-computed style classes
  const styles = useMemo(() => ({
    containerPadding: isMobile ? 'p-4' : 'p-6 lg:p-8',
    cardPadding: isMobile ? 'p-3' : 'p-4',
    itemGap: isMobile ? 'gap-2' : 'gap-4',
    headerHeight: 'h-[56px] md:h-[72px]',
    bodyTextSize: isMobile ? 'text-sm' : 'text-base',
    headingTextSize: isMobile ? 'text-lg' : 'text-xl lg:text-2xl',
  }), [isMobile]);

  const value = useMemo<ResponsiveContextValue>(() => ({
    isMobile,
    isTablet,
    isDesktop,
    deviceType,
    isAbove,
    isBelow,
    isBetween,
    screenWidth,
    screenHeight,
    isLandscape,
    isPortrait,
    behavior,
    styles,
  }), [
    isMobile,
    isTablet,
    isDesktop,
    deviceType,
    isAbove,
    isBelow,
    isBetween,
    screenWidth,
    screenHeight,
    isLandscape,
    isPortrait,
    behavior,
    styles,
  ]);

  return (
    <ResponsiveContext.Provider value={value}>
      {children}
    </ResponsiveContext.Provider>
  );
}

/**
 * Hook to access responsive context values
 * @throws Error if used outside ResponsiveProvider
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useResponsive(): ResponsiveContextValue {
  const context = useContext(ResponsiveContext);
  if (context === undefined) {
    throw new Error('useResponsive must be used within a ResponsiveProvider');
  }
  return context;
}

/**
 * Hook for simple mobile check (convenience wrapper)
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useIsMobile(): boolean {
  const { isMobile } = useResponsive();
  return isMobile;
}

/**
 * Hook for behavior flags (convenience wrapper)
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useResponsiveBehavior() {
  const { behavior } = useResponsive();
  return behavior;
}

/**
 * Hook for style helpers (convenience wrapper)
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useResponsiveStyles() {
  const { styles } = useResponsive();
  return styles;
}
